/**
 * Signed-in composition visibility predicate (PWA-3019).
 *
 * Single source of truth for whether a viewer may see a product's raw weights
 * and long/short directions. Extracted from DiscoverV4DetailPage so the detail
 * view and the bulk CSV export cannot drift apart.
 *
 * Scope note: this gates weights and directions ONLY. Tickers, names and
 * exchanges stay visible, and product-level performance, exposure and risk
 * metrics are not gated -- matching what the detail page actually blurs.
 */
import type { ProductMetadata } from "../types/productMetadata";
import type { Visibility } from "../types/subscription";

export interface ViewerEntitlement {
  isCustomer: boolean;
  isBeta: boolean;
  isRetirementAccount: boolean;
}

/**
 * A product the viewer cannot add to a strategy because of product criteria.
 */
export function isAddBlocked(
  meta: ProductMetadata | null | undefined,
  isRetirementAccount: boolean
): boolean {
  return (
    meta?.investible_tag !== "Yes" ||
    (isRetirementAccount && meta?.is_retirement_tag !== "Yes")
  );
}

/**
 * PUBLIC and SUBSCRIBER weights are visible to every signed-in viewer.
 * A partial snapshot restricts securities, not the returned weights/directions.
 * Callers retain their route/share sign-in gates.
 */
export function isCompositionGlossed(params: {
  visibility: Visibility;
  isPartial: boolean;
  addBlocked: boolean;
  isCustomer: boolean;
  isBeta: boolean;
}): boolean {
  if (params.visibility === "PUBLIC" || params.visibility === "SUBSCRIBER") return false;
  return !(params.isCustomer || params.isBeta) && !params.addBlocked;
}

export type GateReason = "non_customer" | "non_subscriber" | null;

export function glossGateReason(
  visibility: Visibility,
  isGlossed: boolean
): GateReason {
  if (!isGlossed) return null;
  return visibility === "SUBSCRIBER" ? "non_subscriber" : "non_customer";
}
